import { Sizes, Variants } from '@/common/types';
import { styled } from '@/stitches.config';
import { ReactComponent as SpinnerIcon } from '@assets/icons/spinner.svg';

export const StyledButton = styled('button', {
  // primary button colors
  $$button: '$colors$darkBlue200',
  $$buttonLight: '$colors$darkBlue300',
  $$buttonDark: '$colors$darkBlue100',
  $$buttonDisabled: '$colors$coolGray100',

  // secondary button colors
  $$buttonOutlined: '$colors$darkBlue200',
  $$buttonOutlinedLight: '$colors$darkBlue300',
  $$buttonOutlinedGray: '$colors$darkBlue800',
  $$buttonOutlinedDark: '$colors$darkBlue200',
  $$buttonOutlinedActive: '$colors$darkBlue700',

  // tertiary button colors
  $$textButton: '$colors$blue300',
  $$textButtonDisabled: '$colors$darkGray300',
  $$textButtonActive: '$colors$blue200',

  // Reset
  alignItems: 'center',
  all: 'unset',
  display: 'flex',
  borderRadius: '$3',
  textAlign: 'center',
  '&::before': {
    boxSizing: 'border-box',
  },
  '&::after': {
    boxSizing: 'border-box',
  },

  // Custom styles
  fontSize: '$md',
  fontWeight: '$bold',
  justifyContent: 'center',

  '&:disabled': {
    background: '$$buttonDisabled',
    color: '$textSecondary',
    pointerEvents: 'none',
  },
  '&:hover': {
    cursor: 'pointer',
  },
  '&:focus': {
    outlineColor: '$focusOutline',
    outlineStyle: 'solid',
    outlineWidth: '$3',
  },

  // Variants
  variants: {
    size: {
      large: {
        px: '$4',
        py: '$4',
        width: '270px',
      },
      medium: {
        px: '$12',
        py: '$4',
        width: 'fit-content',
      },
      small: {
        px: '$4',
        py: '$2',
      },
    },
    variant: {
      primary: {
        alignItems: 'center',
        backgroundColor: '$$buttonLight',
        color: '$textAccent',
        fontWeight: '$bold',
        '&:hover': {
          backgroundColor: '$$button',
        },
        '&:active': {
          backgroundColor: '$$buttonDark',
        },
      },
      secondary: {
        backgroundColor: 'transparent',
        borderColor: '$$buttonOutlinedLight',
        borderStyle: 'solid',
        borderWidth: '$2',
        color: '$$buttonOutlinedLight',
        '&:hover': {
          backgroundColor: '$$buttonOutlinedGray',
          borderColor: '$$buttonOutlined',
          color: '$$buttonOutlined',
        },
        '&:active': {
          backgroundColor: '$$buttonOutlinedActive',
          borderColor: '$$buttonOutlinedDark',
          color: '$$buttonOutlinedDark',
        },
        '&:disabled': {
          backgroundColor: '$white',
          borderColor: '$textSecondary',
        },
      },
      tertiary: {
        alignItems: 'center',
        color: '$$textButton',
        fontWeight: '$medium',
        px: '$0',
        py: '$0',
        width: 'auto',
        '&:hover': {
          textDecoration: 'underline',
        },
        '&:active': {
          color: '$$textButtonActive',
        },
        '&:disabled': {
          background: 'none',
          color: '$$textButtonDisabled',
        },
      },
    },

    compact: {
      true: {
        padding: '$1 $4',
        fontSize: '$md',
        lineHeight: '$6',
      },
    },

    loading: {
      true: {},
    },
  },

  compoundVariants: [
    {
      css: {
        '&:hover': {
          backgroundColor: '$$buttonLight',
          pointerEvents: 'none',
        },
      },
      loading: true,
      variant: Variants.PRIMARY,
    },
    {
      css: {
        fontSize: '$sm',
        px: '$0',
        py: '$0',
        width: 'auto',
      },
      size: Sizes.SMALL,
      variant: Variants.TERTIARY,
    },
  ],

  defaultVariants: {
    size: Sizes.SMALL,
    variant: Variants.PRIMARY,
  },
});

export const StyledLoading = styled(SpinnerIcon, {
  color: 'inherit',
  variants: {
    size: {
      large: {
        maxHeight: '$8',
        maxWidth: '$8',
      },
      medium: {
        maxHeight: '$8',
        maxWidth: '$8',
      },
      small: {
        maxHeight: '$6',
        maxWidth: '$6',
      },
    },
  },
});
